import LoanModal from "./uloanModal.js";
import { $_GET, trans } from "../globalFunctions.js";

export default class ConvertAllConvertiblesModal extends LoanModal {
  constructor() {
    super("convert-all-convertibles-modal", window.currentCompany.id);

    this.$loansContainer = document.getElementById("loans-container");
    this.$availableShares = this.$form.querySelector("[data-available-shares]");
    this.$amount = this.$form.querySelector('[name="amount"]');
    this.formFields = [
      {
        name: "retention",
        varType: "float",
        required: true,
      },
      {
        name: "share_class",
        varType: "int",
        required: true,
      },
      {
        name: "date",
        varType: "string",
        required: true,
      },
      {
        name: "convertibles",
        varType: "array",
        required: true,
        fields: [
          {
            name: "numeration",
            varType: "float",
            mayNotExist: true,
          },
        ],
        validation: (field, data) => {
          this.getActions(field, data);
        },
      },
    ];

    Promise.all([
      this.documentsPromise,
      this.setupShareClasses(),
      this.getPublicValuation(""),
    ]).then(async (responses) => {
      this.setIssuedShares(responses[2].shares);
      await this.setupConvertibles("");
      this.whenReady();
    });
    this.setupForm();
  }

  whenReady() {
    super.whenReady();
    this.setFloat("retention", 0);
  }

  setupForm() {
    const $date = this.$form.querySelector('[name="date"]');
    $date.valueAsDate = new Date();
    const $dateFilter = this.$form.querySelector('[name="date-filter"]');
    $dateFilter.valueAsDate = new Date();

    $dateFilter.addEventListener("change", () => {
      this.setupConvertibles($dateFilter.value);
    });

    $date.addEventListener("change", async () => {
      const valuation = await this.getPublicValuation($date.value);

      this.setIssuedShares(valuation.shares);

      this.renderConvertibles();
    });

    this.$form
      .querySelector('[name="retention"]')
      .addEventListener("input", () => {
        const retention = this.getFloat("retention");

        if (retention > 100 || retention < 0) {
          this.setFloat("retention", 100);
        }

        this.renderConvertibles();
      });

    this.$form
      .querySelector('[name="premoney"]')
      .addEventListener("input", () => {
        this.renderConvertibles();
      });

    this.$form.querySelectorAll('[name="return-surplus"]').forEach((el) => {
      el.addEventListener("change", () => {
        this.renderConvertibles();
      });
    });
  }

  setIssuedShares(shares) {
    document.getElementById("issued-shares").innerText =
      peque.formatNumber(shares);
  }

  wantsToReturnSurplus() {
    return (
      parseInt(
        this.$form.querySelector('[name="return-surplus"]:checked').value
      ) === 1
    );
  }

  setupConvertibles(date) {
    return new Promise((resolve, reject) => {
      peque
        .api(
          "loans",
          {
            company: this.companyId,
            simulation: $_GET("simulation"),
            resolution_type: 0,
            date: date,
            full: true,
          },
          "get"
        )
        .then((response) => {
          this.convertiblesList = response.result.list;
          this.renderConvertibles();

          resolve();
        });
    });
  }

  renderConvertibles() {
    this.$loansContainer.innerHTML = "";
    const premoney = this.getFloat("premoney");
    const retention = this.getFloat("retention");
    const wantsToReturnSurplus = this.wantsToReturnSurplus();

    let postMoneyPercentage = 0;
    let preMoneyShares = 0;

    // to properly calculate post-money SAFEs, we have to first calculate pre-money SAFEs and the reserved
    // ownership of the post-money ones.
    this.convertiblesList.forEach((convertible, i) => {
      if (typeof convertible.valuationType === "undefined") {
        convertible.valuationType = "premoney";
      }

      const conclusion = this.calculateConclusion(
        convertible,
        premoney,
        retention,
        wantsToReturnSurplus,
        convertible.valuationType
      );

      if (convertible.valuationType === "premoney") {
        preMoneyShares += conclusion.data.shares;
      } else if (
        conclusion.data.appliedValuation === convertible.valuation_cap
      ) {
        const expectedPercentage =
          (conclusion.amountToConvert / conclusion.data.appliedValuation) * 100;
        postMoneyPercentage += expectedPercentage;
        this.convertiblesList[i].expectedPercentage = expectedPercentage;
      }
    });

    // https://www.capboard.io/en/captable/safes-examples
    this.valuation.existingSharesAndPreMoneyShares =
      this.valuation.shares + preMoneyShares;
    this.valuation.postMoneyShares = Math.round(
      this.valuation.existingSharesAndPreMoneyShares /
        (1 - postMoneyPercentage / 100) -
        this.valuation.existingSharesAndPreMoneyShares
    );

    this.convertiblesList.forEach((convertible, i) => {
      this.renderConvertible(
        i,
        convertible,
        premoney,
        retention,
        wantsToReturnSurplus
      );
    });
  }

  renderConvertible(i, convertible, premoney, retention, wantsToReturnSurplus) {
    const conclusion = this.calculateConclusion(
      convertible,
      premoney,
      retention,
      wantsToReturnSurplus,
      convertible.valuationType
    );
    const phrase = this.buildSummaryPhrase(
      convertible,
      conclusion.data.appliedCap,
      conclusion.toReturn,
      conclusion.data.shares,
      conclusion.data.appliedValuation,
      peque.formatCurrency(conclusion.data.sharePrice)
    );

    const template = peque.parseTemplate("loan-summary", {
      amountToConvert: conclusion.amountToConvert,
      convertible,
      phrase,
    });

    const dataValuationTypeGroup = template.querySelector(
      "[data-valuation-type-group]"
    );

    template
      .querySelectorAll('[name="type-' + convertible.id + '"]')
      .forEach((el) => {
        el.addEventListener("change", () => {
          if (el.value === "shares") {
            dataValuationTypeGroup.classList.remove("d-none");
          } else {
            dataValuationTypeGroup.classList.add("d-none");
          }

          const $phrase = template.querySelector("[data-phrase]");
          $phrase.innerText =
            el.value === "shares"
              ? phrase
              : trans("payback_summary_phrase", {
                  payoutAmount: peque.formatCurrency(conclusion.amountToConvert, false, 2, false, 2),
                });
        });
      });

    template
      .querySelectorAll(`[name="valuation-type-${convertible.id}"]`)
      .forEach((el) => {
        el.checked = el.value === convertible.valuationType;
        el.addEventListener("change", (e) => {
          this.convertiblesList[i].valuationType = el.value;
          this.renderConvertibles();
        });
      });

    this.$loansContainer.appendChild(template);

    return conclusion;
  }

  calculateConclusion(
    convertible,
    premoney,
    retention,
    wantsToReturnSurplus,
    valuationType
  ) {
    const earnedInterest = this.calculateEarnedInterest(convertible);
    const amountToConvert = this.calculateAmountToConvert(
      convertible.amount,
      earnedInterest,
      retention
    );
    const data = this.calculateShares(
      this.valuation,
      convertible,
      premoney,
      amountToConvert,
      valuationType
    );
    const toReturn = wantsToReturnSurplus ? data.toReturn : 0;

    return {
      earnedInterest,
      amountToConvert,
      data,
      toReturn,
    };
  }

  getActions(field, fieldData) {
    const premoney = this.getFloat("premoney");
    const retention = this.getFloat("retention");
    const wantsToReturnSurplus = this.wantsToReturnSurplus();

    this.convertiblesList.forEach((convertible) => {
      const conclusion = this.calculateConclusion(
        convertible,
        premoney,
        retention,
        wantsToReturnSurplus,
        convertible.valuationType
      );
      const action = this.$form.querySelector(
        '[name="type-' + convertible.id + '"]:checked'
      ).value;

      fieldData.convertibles.push({
        id: convertible.id,
        action: action,
        amount: conclusion.data.shares,
        return:
          action === "shares"
            ? conclusion.toReturn
            : conclusion.amountToConvert,
      });
    });
  }

  validateAndSendForm() {
    this.enableSubmitButton(false);

    this.getFields(this.formFields)
      .then((data) => {
        data.company = this.companyId;
        data.simulation =
          typeof window.pequeData.simulation === "undefined"
            ? $_GET("simulation")
            : window.pequeData.simulation;

        peque
          .api("loans/convert_all", JSON.stringify(data), "post")
          .then((response) => {
            this.mutateReactTransactions();
          })
          .catch((response) => {
            this.enableSubmitButton(true);

            if (typeof response.field !== "undefined") {
              this.showErrorOnField(
                response.result,
                this.$form.querySelector('[name="' + response.field + '"]')
              );
            } else if (response.error === peque.ERROR_INVALID_DATA) {
              peque.navigation.showError(response.result);
            }
          });
      })
      .catch((error) => {
        this.enableSubmitButton(true);
        if (typeof error.el === "undefined") {
          console.error(error);
          return;
        }
        this.showErrorOnField(error.message, error.el);
      });
  }
}
